import type { ProfileT } from "@patient-watch/types/database";
import type { ChangePasswordRouteSearch } from "@/features/auth/search/authRouteSearch";
import type { AuthCopy } from "@/features/i18n/authCopy";
import { readStoredLocale, resolveLocale, type SupportedLocale } from "@patient-watch/i18n";
import type { RolesT } from "@/utils/roles";
import { needsPasswordSetup } from "./inviteAccountSetup";
import { buildOnboardingTodos } from "@/features/onboarding/journeyTodos";
import {
    getOnboardingRouteForTodoKey,
    profileCopyToOnboardingLabels,
    type PatientResumeDestination,
} from "@/features/onboarding/utils/getPatientResumeDestination";

export type AppHomePath = "/diaries" | "/patients" | "/studies" | "/admin" | "/validate";

export interface PublicAuthLanding {
    to: string;
    search?: ChangePasswordRouteSearch;
    title: string;
    description: string;
    buttonText: string;
    isFirstTime: boolean;
}

function computeProfileIncomplete(profile: ProfileT, roles: RolesT, locale: SupportedLocale = "en-GB"): boolean {
    if (roles.hasRole === false) return true;

    const labels = profileCopyToOnboardingLabels(locale);
    const onboardingIncomplete = buildOnboardingTodos(profile, labels).some((item) => !item.done);

    if (roles.isPatient) {
        return onboardingIncomplete;
    }

    if (!profile.organisations?.length) return true;

    const noName = !profile.title || !profile.first_name || !profile.last_name;

    if (roles.isClinician || roles.isSecretary || roles.isManagerOnly) {
        return noName || profile.email === null;
    }

    return false;
}

function patientOnboardingLanding(copy: AuthCopy, profile: ProfileT, locale: SupportedLocale): PublicAuthLanding {
    const labels = profileCopyToOnboardingLabels(locale);
    const firstIncomplete = buildOnboardingTodos(profile, labels).find((item) => !item.done);
    const inviter = profile.invited_by_name?.trim();
    const invitedLine = inviter ? copy.invitedByLine(inviter) : copy.welcomeFirstTime;

    return {
        to: getOnboardingRouteForTodoKey(firstIncomplete?.key ?? "name"),
        title: copy.welcomeToPatientWatch,
        description: `${invitedLine} ${copy.finishProfileSetup}`,
        buttonText: copy.continueToProfileSetup,
        isFirstTime: true,
    };
}

/** Map a single role key (e.g. from join-org / set-role) to the app home. */
export function getAppHomeFromRoleKey(role: string | null | undefined): AppHomePath {
    const normalised = role?.toLowerCase();
    if (normalised === "patient") return "/diaries";
    if (normalised === "admin") return "/admin";
    if (normalised === "manager") return "/studies";
    return "/patients";
}

/** Primary app destination after login when profile setup is already complete. */
export function getOnboardedAppHome(
    roles: RolesT,
    resume?: Pick<PatientResumeDestination, "to">
): AppHomePath | string {
    if (roles.isPatient && resume?.to) {
        return resume.to;
    }
    if (roles.isPatient) return "/diaries";
    if (roles.isAdmin) return "/admin";
    if (roles.isManager) return "/studies";
    if (roles.isValidator) return "/validate";
    if (roles.isClinician || roles.isSecretary) return "/patients";
    return "/patients";
}

export function getAuthenticatedPublicLanding(
    profile: ProfileT,
    roles: RolesT,
    copy: AuthCopy,
    locale: SupportedLocale = resolveLocale({ storedLocale: readStoredLocale() })
): PublicAuthLanding {
    const inviter = profile.invited_by_name?.trim();
    const invitedLine = inviter ? copy.invitedByLine(inviter) : copy.welcomeFirstTime;

    if (needsPasswordSetup(profile)) {
        return {
            to: "/change-password",
            search: { source: "confirm-account" },
            title: copy.welcomeToPatientWatch,
            description: `${invitedLine} ${copy.choosePasswordDescription}`,
            buttonText: copy.continue,
            isFirstTime: true,
        };
    }

    if (roles.isPatient && computeProfileIncomplete(profile, roles, locale)) {
        return patientOnboardingLanding(copy, profile, locale);
    }

    if (computeProfileIncomplete(profile, roles, locale)) {
        return {
            to: roles.isClinician ? "/onboarding/clinician/name" : "/onboarding/name",
            title: copy.welcomeToPatientWatch,
            description: `${invitedLine} ${copy.finishProfileSetup}`,
            buttonText: copy.continueToProfileSetup,
            isFirstTime: true,
        };
    }

    return {
        to: getOnboardedAppHome(roles),
        title: copy.alreadySignedIn,
        description: copy.alreadySignedInDescription,
        buttonText: copy.continueToApp,
        isFirstTime: false,
    };
}

/**
 * Destination after saving a password on auth `/change-password`.
 * Uses role + onboarding completeness (invite → onboarding; forgot-password → role home).
 */
export function getPostPasswordDestination(
    profile: ProfileT,
    roles: RolesT,
    copy: AuthCopy,
    options?: {
        /** Paid anonymous clinician signup may not have a DB clinician role yet. */
        treatAsClinician?: boolean;
        locale?: SupportedLocale;
    }
): string {
    const locale = options?.locale ?? resolveLocale({ storedLocale: readStoredLocale() });
    const profileAfterPassword: ProfileT = { ...profile, has_password: true };
    const effectiveRoles: RolesT =
        options?.treatAsClinician === true && roles.isClinician !== true
            ? {
                  ...roles,
                  isClinician: true,
                  hasRole: true,
              }
            : roles;

    return getAuthenticatedPublicLanding(profileAfterPassword, effectiveRoles, copy, locale).to;
}
